import type { ReactNode } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  schema?: Record<string, unknown>;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  children?: ReactNode;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = "website",
  schema,
  noindex,
  publishedTime,
  modifiedTime,
  author,
}: SEOProps) {
  const siteUrl =
    typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_SITE_URL
      ? (import.meta.env.VITE_SITE_URL as string)
      : "";
  const fullCanonical = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:site_name" content="時光逆行駭客" />
      <meta property="og:locale" content="zh_TW" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {author && <meta property="article:author" content={author} />}
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </>
  );
}